import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { getSettings, RumConfig } from './config';

let rumClient: AwsRum | null = null;

/**
 * Initialize the CloudWatch RUM web client from runtime settings.json.
 *
 * No-ops when RUM config is absent (local dev) or when running on localhost,
 * so developers never emit telemetry from their machines.
 */
export async function initializeRum(): Promise<void> {
  try {
    const settings = await getSettings();
    const rum: RumConfig | undefined = settings.rum;

    const isLocal =
      typeof window !== 'undefined' &&
      /^(localhost|127\.0\.0\.1)/.test(window.location.hostname);

    if (!rum || !rum.appMonitorId || isLocal) {
      // eslint-disable-next-line no-console
      console.debug('RUM disabled (local dev or missing config)');
      return;
    }

    const config: AwsRumConfig = {
      sessionSampleRate: 1.0,
      identityPoolId: rum.identityPoolId,
      guestRoleArn: rum.guestRoleArn,
      endpoint: `https://dataplane.rum.${rum.region}.amazonaws.com`,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
    };

    rumClient = new AwsRum(rum.appMonitorId, '1.0.0', rum.region, config);
  } catch (e) {
    // RUM must never break the app — swallow init errors.
    // eslint-disable-next-line no-console
    console.debug('RUM init failed', e);
  }
}

/**
 * Record a custom RUM event. Safe no-op if RUM isn't initialized.
 * Keep payloads small and free of PII (no ticket content — counts/types only).
 */
export function recordEvent(type: string, data: Record<string, unknown> = {}): void {
  try {
    rumClient?.recordEvent(type, data);
  } catch {
    // ignore
  }
}
